import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Sublista indentada con hilo vertical: se ve de un vistazo a qué nivel pertenece cada opción.
const GuideList = styled.div`
    margin-left: 0.55rem;
    padding-left: 0.8rem;
    border-left: 2px solid rgb(222, 226, 230);
`;

export const TaxonomyTreeFilterUI = ({ options = [], value = {}, onChange }) => {
    const {
        macrofamilyId,
        familyId,
        subfamilyIds = new Set()
    } = useMemo(() => value, [value]);

    const onChangeMacrofamily = useCallback((event) => {
        const newMacrofamilyId = Number(event.target.value);
        if (!newMacrofamilyId) {
            onChange({});
            return;
        }

        onChange({
            macrofamilyId: newMacrofamilyId,
            familyId: null,
            subfamilyIds: new Set()
        });
    }, []);

    const onChangeFamily = useCallback((familyIdStr) => {
        const id = familyIdStr ? Number(familyIdStr) : null;
        onChange({
            macrofamilyId,
            familyId: id,
            subfamilyIds: new Set()
        });
    }, [macrofamilyId]);

    const onChangeSubfamily = useCallback((event) => {
        const subfamilyId = Number(event.target.value);
        const checked = event.target.checked;

        const updated = new Set(subfamilyIds);
        if (checked) {
            updated.add(subfamilyId);
        } else {
            updated.delete(subfamilyId);
        }

        onChange({
            macrofamilyId,
            familyId,
            subfamilyIds: updated
        });
    }, [macrofamilyId, familyId, subfamilyIds]);

    const families = useMemo(() => {
        return options.find(m => m.id === macrofamilyId)?.familias || [];
    }, [options, macrofamilyId]);

    const activeCount = subfamilyIds.size || (macrofamilyId ? 1 : 0);

    return (
        <Accordion className="taxonomy-dropdowns categories-accordion" defaultExpanded>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="categories-filter-content"
            >
                <span>Categorías</span>
                {activeCount > 0 && (
                    <span className="badge rounded-pill bg-primary ms-2">
                        {activeCount}
                    </span>
                )}
            </AccordionSummary>
            <AccordionDetails>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="macrofamily"
                        id="macro-all"
                        checked={!macrofamilyId}
                        onChange={() => onChangeMacrofamily({ target: { value: '' } })}
                    />
                    <label className="form-check-label" htmlFor="macro-all">
                        Todas las categorías
                    </label>
                </div>

                {options.map(macro => (
                    <React.Fragment key={macro.id}>
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="macrofamily"
                                id={`macro-${macro.id}`}
                                value={macro.id}
                                checked={macrofamilyId === macro.id}
                                onChange={onChangeMacrofamily}
                            />
                            <label
                                className={`form-check-label ${macrofamilyId === macro.id ? 'fw-semibold' : ''}`}
                                htmlFor={`macro-${macro.id}`}
                            >
                                {macro.macrofamilia}
                            </label>
                        </div>

                        {macrofamilyId === macro.id && (
                            <GuideList>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="family"
                                        id="family-all"
                                        checked={!familyId}
                                        onChange={() => onChangeFamily(null)}
                                    />
                                    <label className="form-check-label" htmlFor="family-all">
                                        <small>Todas</small>
                                    </label>
                                </div>

                                {families.map(family => (
                                    <React.Fragment key={family.id}>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="family"
                                                id={`family-${family.id}`}
                                                value={family.id}
                                                checked={familyId === family.id}
                                                onChange={event => onChangeFamily(event.target.value)}
                                            />
                                            <label
                                                className={`form-check-label ${familyId === family.id ? 'fw-semibold' : ''}`}
                                                htmlFor={`family-${family.id}`}
                                            >
                                                <small>{family.familia}</small>
                                            </label>
                                        </div>

                                        {familyId === family.id && family.subfamilias.length > 0 && (
                                            <GuideList>
                                                {family.subfamilias.map(subfamily => (
                                                    <div key={subfamily.id} className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`subfamily-${subfamily.id}`}
                                                            value={subfamily.id}
                                                            checked={subfamilyIds.has(subfamily.id)}
                                                            onChange={onChangeSubfamily}
                                                        />
                                                        <label
                                                            className={`form-check-label ${subfamilyIds.has(subfamily.id) ? 'fw-semibold' : ''}`}
                                                            htmlFor={`subfamily-${subfamily.id}`}
                                                        >
                                                            <small>{subfamily.subfamilia}</small>
                                                        </label>
                                                    </div>
                                                ))}
                                            </GuideList>
                                        )}
                                    </React.Fragment>
                                ))}
                            </GuideList>
                        )}
                    </React.Fragment>
                ))}
            </AccordionDetails>
        </Accordion>
    );
};
